import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { downloadPdfFromSpec } from "../pdf/pdfCore";
import FavouriteButton from "./FavouriteButton";
import {
  buildCustomerStockPdfSpec,
  buildCustomerStockRows,
  buildCylInfoMap,
  computeAvailableCounts,
  exportCustomerStockExcel,
  filterCustomerStockRows,
  reportDateLabel,
  totalAvailable,
} from "./customerStockReportHelper";

const CustomerStockReport = () => {
  const [rows, setRows] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [generatedAt, setGeneratedAt] = useState(() => new Date());

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      // Load both collections once, then join in memory (no per-row lookups).
      const [warehouseSnap, cylInfoSnap] = await Promise.all([
        getDocs(collection(db, "cylinders_warehouse")),
        getDocs(collection(db, "cyl_info")),
      ]);

      const warehouseRecords = warehouseSnap.docs.map((d) => d.data());
      const cylInfoDocuments = cylInfoSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const counts = computeAvailableCounts(warehouseRecords);
      const cylInfoMap = buildCylInfoMap(cylInfoDocuments);
      const result = buildCustomerStockRows({ counts, cylInfoMap });

      setRows(result.rows);
      setUnmatched(result.unmatched);
      setGeneratedAt(new Date());
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleRows = useMemo(
    () => filterCustomerStockRows(rows, search),
    [rows, search]
  );

  const totalItems = useMemo(() => totalAvailable(rows), [rows]);
  const unmatchedItems = useMemo(() => totalAvailable(unmatched), [unmatched]);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-6">
      <FavouriteButton pageUrl={window.location.pathname} />

      <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-5xl border border-gray-300">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Cylinder Stock Availability</h2>
          <p className="text-sm text-gray-500 mt-1">Report generated {reportDateLabel(generatedAt)}</p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by product or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[240px] p-3 border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
          />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition disabled:opacity-60"
            >
              Refresh
            </button>
            <button
              onClick={() => exportCustomerStockExcel(rows, generatedAt)}
              disabled={loading || rows.length === 0}
              className="bg-primary hover:bg-hover text-white px-4 py-2 rounded-md transition disabled:opacity-60"
            >
              Export to Excel
            </button>
            <button
              onClick={() => downloadPdfFromSpec(buildCustomerStockPdfSpec(rows, generatedAt))}
              disabled={loading || rows.length === 0}
              className="bg-primary hover:bg-hover text-white px-4 py-2 rounded-md transition disabled:opacity-60"
            >
              Download PDF
            </button>
          </div>
        </div>

        {/* Summary */}
        {!loading && !error && (
          <p className="text-sm text-gray-600 mb-3">
            {rows.length} product{rows.length === 1 ? "" : "s"} in stock &middot; {totalItems} cylinder
            {totalItems === 1 ? "" : "s"} available
          </p>
        )}

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Internal-only note for stock we couldn't describe */}
        {!loading && unmatched.length > 0 && (
          <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            {unmatchedItems} available cylinder{unmatchedItems === 1 ? "" : "s"} could not be matched to
            product info and are hidden from this report. Check cyl_info for the missing product codes.
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full bg-white shadow-md rounded-lg">
            <thead className="bg-primary text-white">
              <tr>
                <th className="p-3 text-left">Product</th>
                <th className="p-3 text-left">Product Code</th>
                <th className="p-3 text-right">Available</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="p-6 text-center text-gray-500">
                    Loading stock...
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-6 text-center text-gray-500">
                    {rows.length === 0 ? "No stock currently available." : "No products match your search."}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  <tr key={row.productId} className="border-t hover:bg-gray-100 transition">
                    <td className="p-3">{row.productDescription}</td>
                    <td className="p-3">{row.productCode}</td>
                    <td className="p-3 text-right">{row.available}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomerStockReport;
